import forEach from 'lodash/forEach';
import get from 'lodash/get';
import keyBy from 'lodash/keyBy';
import { getNpcDialogue } from '~lib/common/utils/npc';
import { getAllCharacters, targetCharacterKeyOrDefault } from '~lib/common/utils/characters';
import isTagOnlyText from '~lib/common/utils/isTagOnlyText';
import { TAG_ONLY_ANSWER_MESSAGE } from '../dialogue/validateDialogue';
import hasActionOrTimerConnection from './validateActionOrTimerConnection';
import { caseLogicMsg } from '../validationMessages';

// Prompt effects saved before source characters existed have no
// sourceCharacterKey but do have prompt content.
// Default those to Patient so an existing case doesn't error. 
// A prompt effect with neither content nor a source is a new node still awaiting a selection.
const DEFAULT_PROMPT_SOURCE_CHARACTER_KEY = 'patient';

function promptSourceCharacterKeyOrDefault(effect) {
  if (effect.sourceCharacterKey) {
    return effect.sourceCharacterKey;
  }
  return get(effect, 'prompt.markup') ? DEFAULT_PROMPT_SOURCE_CHARACTER_KEY : effect.sourceCharacterKey;
}

function validateAiNodes(caseConf, nodeInfo) {
  let aiQuestions = get(caseConf, 'dialogue.aiQuestions', []) || [];
  let aiUnsolicited = get(caseConf, 'dialogue.aiUnsolicited', []) || [];
  const npcDialogue = getNpcDialogue(caseConf) || {};
  const npcAiQuestions = npcDialogue.aiQuestions || [];
  if (npcAiQuestions.length > 0) aiQuestions = [...aiQuestions, ...npcAiQuestions];
  const npcAiUnsolicited = npcDialogue.aiUnsolicited || [];
  if (npcAiUnsolicited.length > 0) aiUnsolicited = [...aiUnsolicited, ...npcAiUnsolicited];
  const questionsStatements = [...aiQuestions, ...aiUnsolicited];
  const questionsStatementsById = keyBy(questionsStatements, 'id');
  const questionsById = keyBy(aiQuestions, 'id');
  const unsolicitedById = keyBy(aiUnsolicited, 'id');
  const characters = getAllCharacters(caseConf);
  const charactersByKey = keyBy(characters, 'key');

  const errors = [];
  const warnings = [];

  // Action nodes
  const aiActionTypes = ['AI_QUESTION', 'NPC_AI_QUESTION'];

  nodeInfo.actionNodes.forEach((node) => {
    const action = get(node, 'data.action');
    if (action && aiActionTypes.includes(action.type)) {
      if (action.characterKey && !charactersByKey[action.characterKey]) {
        errors.push(caseLogicMsg('Character could not be found', node.id, { errorId: 'missingSourceCharacter' }));
      }
      if (!action.id) {
        errors.push(caseLogicMsg('You need to select a question', node.id, { errorId: 'missingQuestion' }));
      } else if (!questionsById[action.id]) {
        errors.push(caseLogicMsg('Question could not be found', node.id, { errorId: 'invalidQuestion' }));
      }
      if (!action.confidence && action.confidence !== 0) {
        errors.push(caseLogicMsg('You need to set confidence', node.id, { errorId: 'missingConfidence' }));
      }
      else if (action.confidence > 100 || action.confidence < 1) {
        errors.push(caseLogicMsg('Confidence value must be 1-100', node.id, { errorId: 'missingConfidence' }));
      }
    }
  });

  const connectedUnsolicited = [];
  // Effect nodes
  nodeInfo.effectNodes.forEach((node, effectKey) => {
    const effect = get(node, 'data.effect');
    if (!effect) {
      return;
    }
    if (effect.type === 'AI_ANSWER' || effect.type === 'NPC_AI_ANSWER') {
      if (!effect.id && !effect.sourceCharacterKey) {
        errors.push(caseLogicMsg('You need to select a character', node.id, { errorId: 'missingSourceCharacter' }));
      }
      if (effect.sourceCharacterKey && !charactersByKey[effect.sourceCharacterKey]) {
        errors.push(caseLogicMsg('Character could not be found', node.id, { errorId: 'missingSourceCharacter' }));
      }
      if (!effect.id) {
        errors.push(caseLogicMsg('You need to select a question', node.id, { errorId: 'missingQuestion' }));
        return;
      }
      if (!questionsById[effect.id]) {
        errors.push(caseLogicMsg('Question could not be found', node.id, { errorId: 'invalidQuestion' }));
        return;
      }
      if (effect.response !== 'AI' && isTagOnlyText(effect.value)) {
        errors.push(caseLogicMsg(TAG_ONLY_ANSWER_MESSAGE, node.id, { errorId: 'tagOnlyAnswer' }));
      }
      if (!effect.reason) {
        errors.push(caseLogicMsg('Provide AI with reason for response', node.id, { errorId: 'missingReason' }));
        return;
      }
    }

    if (effect.type === 'AI_UNSOLICITED_DIALOGUE') {
      const targetCharacterKey = targetCharacterKeyOrDefault(effect.targetCharacterKey);
      if (hasActionOrTimerConnection(node, nodeInfo.nodeMap)) {
        connectedUnsolicited.push({ id: effect.id, targetCharacterKey });
      }
      const unsolicited = unsolicitedById[effect.id] || {};
      const answer = get(unsolicited, 'options.answer.en');
      if (!effect.id && !effect.sourceCharacterKey) {
        errors.push(caseLogicMsg('You need to select a source character', node.id, { errorId: 'missingSourceCharacter' }));
      }
      if (effect.sourceCharacterKey && !charactersByKey[effect.sourceCharacterKey]) {
        errors.push(caseLogicMsg('Source character could not be found', node.id, { errorId: 'missingSourceCharacter' }));
      }
      if (effect.sourceCharacterKey && !effect.id) {
        errors.push(caseLogicMsg('You need to select a statement', node.id, {effectKey, errorId: 'missingDialogueItem' }));
      }
      if (effect.id && !answer) {
        errors.push(caseLogicMsg('Statement could not be found', node.id, {effectKey, errorId: 'missingDialogueItem' }));
      }
      if (unsolicited && !targetCharacterKey) {
        errors.push(caseLogicMsg('You need to select a target character', node.id, { errorId: 'missingTargetCharacter' }));
      }
      if (targetCharacterKey && !charactersByKey[targetCharacterKey]) {
        errors.push(caseLogicMsg('Target character could not be found', node.id, { errorId: 'missingTargetCharacter' }));
      }
    }
    if (effect.type === 'AI_UPDATE_PROMPT') {
      const sourceCharacterKey = promptSourceCharacterKeyOrDefault(effect);
      if (!effect.id && !sourceCharacterKey) {
        errors.push(caseLogicMsg('You need to select a character', node.id, { errorId: 'missingSourceCharacter' }));
      }
      if (sourceCharacterKey && !charactersByKey[sourceCharacterKey]) {
        errors.push(caseLogicMsg('Character could not be found', node.id, { errorId: 'missingSourceCharacter' }));
      }
      if (!effect.reason) {
        errors.push(caseLogicMsg('Provide AI with reason for response', node.id));
        return;
      }
    }
  });

  const dialogueTypes = {
    AI_QUESTION: 'Question/Statement',
    NPC_AI_QUESTION: 'Question/Statement',
    AI_UNSOLICITED_DIALOGUE: 'Unsolicited',
    NPC_AI_UNSOLICITED_DIALOGUE: 'Unsolicited'
  }

  // Check nodes
  const aiCheckTypes = ['AI_QUESTION', 'AI_UNSOLICITED_DIALOGUE', 'NPC_AI_QUESTION', 'NPC_AI_UNSOLICITED_DIALOGUE'];
  nodeInfo.checkNodes.forEach((node) => {
    forEach(node.data, (check, checkKey) => {
      if (aiCheckTypes.includes(check.type)) {
        if (!check.id && !check.sourceCharacterKey) {
          errors.push(caseLogicMsg('You need to select a Source Character', node.id, { checkKey, errorId: 'missingSourceCharacter' }));
        }
        if (check.sourceCharacterKey && !charactersByKey[check.sourceCharacterKey]) {
          errors.push(caseLogicMsg('Source Character could not be found', node.id, { checkKey, errorId: 'missingSourceCharacter' }));
        }
        if (check.sourceCharacterKey && !check.id) {
          errors.push(caseLogicMsg(`You need to select a ${dialogueTypes[check.type].toLowerCase()}`, node.id, { checkKey, errorId: 'missingDialogue' }));
        } else if (!questionsStatementsById[check.id]) {
          errors.push(caseLogicMsg(`${dialogueTypes[check.type]} could not be found`, node.id, { checkKey, errorId: 'invalidQuestion' }));
        }
        if (['AI_QUESTION', 'NPC_AI_QUESTION'].includes(check.type)) {
          if (check.id && (!check.confidence && check.confidence !== 0)) {
            errors.push(caseLogicMsg('You need to set confidence', node.id, { checkKey, errorId: 'missingConfidence' }));
          }
          else if (check.id && (check.confidence > 100 || check.confidence < 1)) {
            errors.push(caseLogicMsg('Confidence value must be 1-100', node.id, { checkKey, errorId: 'missingConfidence' }));
          }
        }
        if (['AI_UNSOLICITED_DIALOGUE', 'NPC_AI_UNSOLICITED_DIALOGUE'].includes(check.type)) {
          const targetCharacterKey = targetCharacterKeyOrDefault(check.targetCharacterKey);
          if (check.id && !targetCharacterKey) {
            errors.push(caseLogicMsg('You need to select a Target Character', node.id, { checkKey, errorId: 'missingTargetCharacter' }));
          }
          if (targetCharacterKey && !charactersByKey[targetCharacterKey]) {
            errors.push(caseLogicMsg('Target Character could not be found', node.id, { checkKey, errorId: 'missingSourceCharacter' }));
          }
          if (!connectedUnsolicited.some(c => c.id === check.id && c.targetCharacterKey === targetCharacterKey)) {
            const dialogueTitle = dialogueTypes[check.type];
            errors.push(caseLogicMsg(`Use this ${dialogueTitle} in an Effect with same Target Character, connected to an Action or Timer`, node.id, { checkKey, errorId: 'invalidUnsolicited', checkId: check.id }));
          }
        }
      }
    });
  });

  return {
    errors,
    warnings,
  };
}

export default validateAiNodes;